import React, { useState, useEffect, useCallback } from 'react';
import { Button, Box, CircularProgress } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import DialogModal from '../../../components/DialogModal';
import ConfirmationDialog from '../../../components/ConfirmationDialog/ConfirmationDialog';
import PaymentDetailsTable from '../components/PaymentDetailsTable';
import { StudentTransactionMatrixItem } from '../../../types/paymentScheme';
import { SnackbarSeverityType } from '../../../enums';
import { normalizeSelection } from '../../../utils/gridSelection';
import paymentsApis from '../../../apis/payments/payments';
import OverlayLoader from '../../../components/OverlayLoader/OverlayLoader';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CustomDatePicker from '../../../components/DatePicker/CustomDatePicker';
import dayjs, { Dayjs } from 'dayjs';
import TextInputWithIcon from '../../../components/TextInputWithIcon';

interface CancelTransactionsDialogProps {
  open: boolean;
  onClose: () => void;
  onShowToast: (message: string, severity: SnackbarSeverityType) => void;
  onUpdate: () => void;
  transactions: StudentTransactionMatrixItem[];
}

const CancelTransactionsDialog: React.FC<CancelTransactionsDialogProps> = ({
  open,
  onClose,
  onShowToast,
  onUpdate,
  transactions
}) => {
  const [selectedTransactionRows, setSelectedTransactionRows] = useState<
    string[]
  >([]);
  const [cancellationRemark, setCancellationRemark] = useState('');
  const [cancellationDate, setCancellationDate] = useState<Dayjs | null>(
    dayjs()
  );
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const resetState = useCallback(() => {
    setSelectedTransactionRows([]);
    setCancellationRemark('');
    setCancellationDate(dayjs());
    setIsCancelling(false);
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const handleTransactionRowSelection = (
    newSelection: GridRowSelectionModel
  ) => {
    setSelectedTransactionRows(
      normalizeSelection(
        newSelection,
        transactions.map((r) => r._id)
      )
    );
  };

  const handleCancelTransactions = async () => {
    setIsConfirmDialogOpen(false);
    setIsCancelling(true);
    try {
      const receiptNos = Array.from(
        new Set(
          transactions
            .filter((transaction) =>
              selectedTransactionRows.includes(transaction._id)
            )
            .map((transaction) => String(transaction.receiptNo))
        )
      );

      if (receiptNos.length === 0) {
        onShowToast(
          'No transactions selected for cancellation.',
          SnackbarSeverityType.warning
        );
        return;
      }

      if (!cancellationDate) {
        onShowToast(
          'Cancellation date is required.',
          SnackbarSeverityType.error
        );
        return;
      }

      await paymentsApis.cancelTransactions({
        receiptNos: receiptNos,
        remark: cancellationRemark,
        statusChangeDate: cancellationDate?.format('YYYY-MM-DD')
      });

      onShowToast(
        'Transactions cancelled successfully!',
        SnackbarSeverityType.success
      );
      onUpdate();
      onClose();
      resetState();
    } catch (error: any) {
      console.error('Error cancelling transactions:', error);
      onShowToast(
        error?.errors?.[0]?.message || 'Failed to cancel transactions.',
        SnackbarSeverityType.error
      );
    } finally {
      setIsCancelling(false);
    }
  };

  const isCancelButtonDisabled =
    selectedTransactionRows.length === 0 || !cancellationDate || isCancelling;

  const dialogActions = (
    <>
      <Button onClick={onClose} color='primary' variant='contained'>
        Close
      </Button>
      <Button
        onClick={() => setIsConfirmDialogOpen(true)}
        color='error'
        variant='contained'
        startIcon={
          isCancelling ? <CircularProgress size={20} color='inherit' /> : null
        }
        disabled={isCancelButtonDisabled}
      >
        Cancel Selected Payments
      </Button>
    </>
  );

  return (
    <>
      <DialogModal
        open={open}
        onClose={onClose}
        title='Cancel Multiple Transactions'
        maxWidth='md'
        actions={dialogActions}
      >
        <OverlayLoader show={isCancelling} />
        <Box mb={2}>
          <PaymentDetailsTable
            title='Select Transactions to Cancel'
            feeDetails={transactions}
            isLoading={false}
            checkboxSelection={true}
            onRowSelectionModelChange={handleTransactionRowSelection}
          />
        </Box>
        <Box display='flex' gap='16px' mt={2}>
          <TextInputWithIcon
            label='Cancellation Remark'
            placeholder='Enter Remark'
            value={cancellationRemark}
            onChange={(e) => setCancellationRemark(e.target.value)}
            fullWidth
            rows={2}
            hasTopLabel
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <CustomDatePicker
              label='Cancellation Date'
              required={false}
              value={cancellationDate}
              onChange={(newValue: Dayjs | null) =>
                setCancellationDate(newValue)
              }
            />
          </LocalizationProvider>
        </Box>
      </DialogModal>

      <ConfirmationDialog
        open={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
        onConfirm={handleCancelTransactions}
        message='Are you sure you want to cancel the selected transactions?'
      />
    </>
  );
};

export default CancelTransactionsDialog;
